import { useState, useEffect } from 'react';
import { Box, Button, CircularProgress, Tooltip, Typography, Chip, Paper, Divider, 
  Select, MenuItem, FormControl, InputLabel, Stack, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField } from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ImageIcon from '@mui/icons-material/Image';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import LinkIcon from '@mui/icons-material/Link';
import { booksAPI } from '../../services/api';

const BookCover = ({ book, onStatusChange, setSnackbar }) => {
  const [coverUpdateLoading, setCoverUpdateLoading] = useState(false);
  const [thumbnailUpdateLoading, setThumbnailUpdateLoading] = useState(false);
  const [coverSource, setCoverSource] = useState('');
  const [thumbnailSource, setThumbnailSource] = useState('');
  const [coverZoomLevel, setCoverZoomLevel] = useState(3);
  const [thumbnailZoomLevel, setThumbnailZoomLevel] = useState(1);

  // Add new state for URL dialog
  const [urlDialogOpen, setUrlDialogOpen] = useState(false);
  const [externalImageUrl, setExternalImageUrl] = useState('');
  const [isForCover, setIsForCover] = useState(true); // true for cover, false for thumbnail
  const [urlError, setUrlError] = useState('');

  // Determine the source of images when component mounts or book changes
  useEffect(() => {
    if (book) {
      // Determine cover source
      if (book.cover) {
        if (book.cover.includes('openlibrary.org')) {
          setCoverSource('OpenLibrary');
        } else if (book.cover.includes('google')) {
          setCoverSource('Google Books');
        } else {
          setCoverSource('Custom');
        }
      } else {
        setCoverSource('None');
      }
      
      // Determine thumbnail source
      if (book.thumbnail) {
        if (book.thumbnail.includes('openlibrary.org')) {
          setThumbnailSource('OpenLibrary');
        } else if (book.thumbnail.includes('google')) {
          setThumbnailSource('Google Books');
        } else {
          setThumbnailSource('Custom');
        }
      } else {
        setThumbnailSource('None');
      }
    }
  }, [book]);

  // Function to search Google Books API by ISBN
  const searchGoogleBooksByIsbn = async (isbn) => {
    try {
      // Make a request to Google Books API
      const url = `https://www.googleapis.com/books/v1/volumes?q=isbn:${isbn}&key=${process.env.REACT_APP_GOOGLE_BOOKS_API_KEY || ''}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error('Failed to fetch data from Google Books');
      }
      
      const data = await response.json();
      
      if (!data.items || data.items.length === 0) {
        throw new Error('No books found with this ISBN in Google Books');
      }
      
      const bookItem = data.items[0];
      const imageLinks = bookItem.volumeInfo?.imageLinks;
      
      if (!imageLinks) {
        throw new Error('No images available for this book');
      }
      
      return {
        thumbnail: imageLinks.thumbnail,
        smallThumbnail: imageLinks.smallThumbnail,
        // Some books have larger image variants
        extraLarge: imageLinks.extraLarge,
        large: imageLinks.large,
        medium: imageLinks.medium
      };
    } catch (error) {
      throw error;
    }
  };

  // Function to update the book cover using Google Books and URL-based upload
  const handleCoverUpdate = async () => {
    if (!book.isbn) {
      setSnackbar({
        open: true,
        message: 'ISBN is required to fetch the cover',
        severity: 'error'
      });
      return;
    }
    
    try {
      setCoverUpdateLoading(true);
      
      // Get image links from Google Books
      const imageLinks = await searchGoogleBooksByIsbn(book.isbn);
      
      // Choose the best available image size for cover (in order of preference)
      let coverUrl = imageLinks.extraLarge || 
                     imageLinks.large || 
                     imageLinks.medium || 
                     imageLinks.thumbnail;
      
      if (!coverUrl) {
        throw new Error('No suitable cover image found');
      }

      // Remove any existing zoom parameter
      coverUrl = coverUrl.replace(/&zoom=\d+/, '');
      
      // Add the selected zoom level
      if (coverUrl.includes('?')) {
        coverUrl += `&zoom=${coverZoomLevel}`;
      } else {
        coverUrl += `?zoom=${coverZoomLevel}`;
      }
      
      // Upload the cover by URL
      const coverUploadResponse = await booksAPI.uploadCoverByUrl(book.id, { url: coverUrl });
      
      // Check if we got a valid response
      const newCoverUrl = coverUploadResponse.data?.url;
      
      // Notify parent component with updated data
      if (onStatusChange && newCoverUrl) {
        onStatusChange(book.status, { 
          ...book, 
          cover: newCoverUrl
        });
      }
      
      setSnackbar({
        open: true,
        message: `Cover updated successfully from Google Books (zoom level: ${coverZoomLevel})`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating cover:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update cover: ' + (error.message || 'Unknown error'),
        severity: 'error'
      });
    } finally {
      setCoverUpdateLoading(false);
    }
  };

  // Function to update the book thumbnail using Google Books and URL-based upload
  const handleThumbnailUpdate = async () => {
    if (!book.isbn) {
      setSnackbar({
        open: true,
        message: 'ISBN is required to fetch the thumbnail',
        severity: 'error'
      });
      return;
    }
    
    try {
      setThumbnailUpdateLoading(true);
      
      // Get image links from Google Books
      const imageLinks = await searchGoogleBooksByIsbn(book.isbn);
      
      // Use the small thumbnail or standard thumbnail as base URL
      let thumbnailUrl = imageLinks.smallThumbnail || imageLinks.thumbnail;
      
      if (!thumbnailUrl) {
        throw new Error('No thumbnail image found');
      }
      
      // Remove any existing zoom parameter
      thumbnailUrl = thumbnailUrl.replace(/&zoom=\d+/, '');
      
      // Add the selected zoom level
      if (thumbnailUrl.includes('?')) {
        thumbnailUrl += `&zoom=${thumbnailZoomLevel}`;
      } else {
        thumbnailUrl += `?zoom=${thumbnailZoomLevel}`;
      }
      
      // Upload the thumbnail by URL
      const thumbnailUploadResponse = await booksAPI.uploadThumbnailByUrl(book.id, { url: thumbnailUrl });
      
      // Check if we got a valid response
      const newThumbnailUrl = thumbnailUploadResponse.data?.url;
      
      // Notify parent component with updated data
      if (onStatusChange && newThumbnailUrl) {
        onStatusChange(book.status, { 
          ...book, 
          thumbnail: newThumbnailUrl
        });
      }
      
      setSnackbar({
        open: true,
        message: `Thumbnail updated successfully from Google Books (zoom level: ${thumbnailZoomLevel})`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating thumbnail:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update thumbnail: ' + (error.message || 'Unknown error'),
        severity: 'error'
      });
    } finally {
      setThumbnailUpdateLoading(false);
    }
  };

  // Function to open the URL dialog
  const handleOpenUrlDialog = (forCover = true) => {
    setIsForCover(forCover);
    setExternalImageUrl('');
    setUrlError('');
    setUrlDialogOpen(true);
  };

  // Function to close the URL dialog
  const handleCloseUrlDialog = () => {
    setUrlDialogOpen(false);
    setExternalImageUrl('');
    setUrlError('');
  };

  // Function to handle URL input change
  const handleUrlChange = (e) => {
    setExternalImageUrl(e.target.value);
    // Simple URL validation
    if (!e.target.value) {
      setUrlError('URL is required');
    } else if (!/^https?:\/\/.*\.(jpg|jpeg|png|webp|gif)$/i.test(e.target.value)) {
      setUrlError('URL should be a direct link to an image (jpg, png, webp, gif)');
    } else {
      setUrlError('');
    }
  };

  // Function to upload image from external URL
  const handleUploadFromUrl = async () => {
    if (!externalImageUrl || urlError) return;
    
    const endpoint = isForCover ? 'uploadCoverByUrl' : 'uploadThumbnailByUrl';
    const loadingStateSetter = isForCover ? setCoverUpdateLoading : setThumbnailUpdateLoading;
    
    try {
      loadingStateSetter(true);
      
      const response = await booksAPI[endpoint](book.id, { url: externalImageUrl });
      
      // Check if we got a valid response
      const newImageUrl = response.data?.url;
      
      // Notify parent component with updated data
      if (onStatusChange && newImageUrl) {
        onStatusChange(book.status, { 
          ...book, 
          [isForCover ? 'cover' : 'thumbnail']: newImageUrl
        });
      }
      
      setSnackbar({
        open: true,
        message: `${isForCover ? 'Cover' : 'Thumbnail'} updated successfully from external URL`,
        severity: 'success'
      });
      
      handleCloseUrlDialog();
    } catch (error) {
      console.error(`Error updating ${isForCover ? 'cover' : 'thumbnail'} from URL:`, error);
      setSnackbar({
        open: true,
        message: `Failed to update ${isForCover ? 'cover' : 'thumbnail'}: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      loadingStateSetter(false);
    }
  };

  // Common image component for both cover and thumbnail
  const ImageContainer = ({ 
    title, 
    imageUrl, 
    altText, 
    isLoading, 
    onUpdate, 
    sourceTag, 
    placeholderText,
    imageStyle,
    zoomLevel,
    setZoomLevel
  }) => (
    <Paper elevation={1} sx={{ mb: 3 }}>
      <Box sx={{ p: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 1.5 
        }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="subtitle1" fontWeight="medium">
              {title}
            </Typography>
            {sourceTag !== 'None' && (
              <Chip 
                label={sourceTag} 
                size="small" 
                color="primary" 
                variant="outlined" 
                icon={<ImageIcon fontSize="small" />} 
              />
            )}
          </Box>
        </Box>
        
        <Divider sx={{ mb: 2 }} />
        
        <Box 
          sx={{ 
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#f5f5f5',
            borderRadius: 1,
            border: '1px solid #ddd',
            padding: 2,
            overflow: 'hidden',
            mb: 2
          }}
        >
          {imageUrl ? (
            <Box 
              component="img" 
              src={imageUrl} 
              alt={altText} 
              sx={imageStyle}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = `https://placehold.co/400x600?text=${encodeURIComponent(placeholderText)}`;
              }}
            />
          ) : (
            <Box 
              component="img" 
              src={`https://placehold.co/400x600?text=${encodeURIComponent(placeholderText)}`} 
              alt={`${altText} (missing)`} 
              sx={imageStyle}
            />
          )}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          {sourceTag === 'None' 
            ? `No ${title.toLowerCase()} available. Please update using the button below.` 
            : `Current ${title.toLowerCase()} from ${sourceTag}.`}
        </Typography>
        
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1 
        }}>
          <FormControl size="small" sx={{ minWidth: 110, flexGrow: 0 }}>
            <InputLabel id={`${title.toLowerCase()}-zoom-label`} sx={{ fontSize: '0.8rem' }}>
              Zoom Level
            </InputLabel>
            <Select
              labelId={`${title.toLowerCase()}-zoom-label`}
              value={zoomLevel}
              label="Zoom Level"
              onChange={(e) => setZoomLevel(e.target.value)}
              disabled={isLoading}
              size="small"
              sx={{ fontSize: '0.8rem' }}
            >
              <MenuItem value={1}>Level 1 (smallest)</MenuItem>
              <MenuItem value={2}>Level 2</MenuItem>
              <MenuItem value={3}>Level 3 (medium)</MenuItem>
              <MenuItem value={4}>Level 4</MenuItem>
              <MenuItem value={5}>Level 5 (largest)</MenuItem>
            </Select>
          </FormControl>
          
          <Box>
            <Tooltip title={`Upload ${title.toLowerCase()} from external URL`}>
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                onClick={() => handleOpenUrlDialog(title === 'Cover')}
                sx={{ fontSize: '0.8rem', height: '2.2rem', mr: 1 }}
                startIcon={<LinkIcon fontSize="small" />}
              >
                From URL
              </Button>
            </Tooltip>
            
            <Tooltip title={`Update ${title.toLowerCase()} from Google Books using ISBN`}>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={onUpdate}
                disabled={isLoading || !book.isbn}
                sx={{ fontSize: '0.8rem', height: '2.2rem' }}
                startIcon={isLoading ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <AutoFixHighIcon fontSize="small" />
                )}
              >
                {isLoading ? 'Updating...' : `Update`}
              </Button>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Paper>
  );

  return (
    <Box>
      <ImageContainer 
        title="Cover"
        imageUrl={book.cover}
        altText={book.title}
        isLoading={coverUpdateLoading}
        onUpdate={handleCoverUpdate}
        sourceTag={coverSource}
        placeholderText="No Cover"
        imageStyle={{ 
          width: '100%',
          maxHeight: '300px',
          objectFit: 'contain'
        }}
        zoomLevel={coverZoomLevel}
        setZoomLevel={setCoverZoomLevel}
      />
      
      <ImageContainer 
        title="Thumbnail"
        imageUrl={book.thumbnail}
        altText={`${book.title} (thumbnail)`}
        isLoading={thumbnailUpdateLoading}
        onUpdate={handleThumbnailUpdate}
        sourceTag={thumbnailSource}
        placeholderText="No Thumbnail"
        imageStyle={{ 
          maxWidth: '120px',
          maxHeight: '120px',
          objectFit: 'contain'
        }}
        zoomLevel={thumbnailZoomLevel}
        setZoomLevel={setThumbnailZoomLevel}
      />

      {/* External URL Dialog */}
      <Dialog open={urlDialogOpen} onClose={handleCloseUrlDialog}>
        <DialogTitle>
          Upload {isForCover ? 'Cover' : 'Thumbnail'} from URL
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="image-url"
            label="Image URL"
            type="url"
            fullWidth
            variant="outlined"
            value={externalImageUrl}
            onChange={handleUrlChange}
            error={!!urlError}
            helperText={urlError || "Enter a direct link to an image file (jpg, png, webp, gif)"}
            placeholder="https://example.com/image.jpg"
            sx={{ mt: 1, minWidth: 350 }}
          />
          {externalImageUrl && !urlError && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Preview:
              </Typography>
              <Box 
                component="img" 
                src={externalImageUrl} 
                alt="Preview"
                sx={{ 
                  maxWidth: '100%', 
                  maxHeight: '200px',
                  objectFit: 'contain',
                  mt: 1,
                  border: '1px solid #eee'
                }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://placehold.co/400x600?text=Invalid+Image';
                  setUrlError('Could not load image from URL. Please check the link.');
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUrlDialog}>Cancel</Button>
          <Button 
            onClick={handleUploadFromUrl} 
            variant="contained" 
            disabled={!externalImageUrl || !!urlError || coverUpdateLoading || thumbnailUpdateLoading}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BookCover;
